import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

export type AlertVariant = 'default' | 'success' | 'warning' | 'info' | 'destructive';

/**
 * Text body = ink token `text-gray-100` (na light themes tmavý, na night světlý).
 * Barevný signál nese levý accent + ikona — pastelové `text-amber-300` na amber
 * wash pozadí dává na primavere/day žlutou na žluté a nejde číst.
 */
const VARIANT_SHELL: Record<AlertVariant, string> = {
  default: 'acm-alert acm-alert-default text-gray-100',
  success: 'acm-alert acm-alert-success text-gray-100',
  warning: 'acm-alert acm-alert-warning text-gray-100',
  info: 'acm-alert acm-alert-info text-gray-100',
  destructive: 'acm-alert acm-alert-destructive text-gray-100',
};

const VARIANT_ACCENT: Record<AlertVariant, string> = {
  default: 'bg-gray-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  info: 'bg-plex',
  destructive: 'bg-red-500',
};

const VARIANT_ICON_COLOR: Record<AlertVariant, string> = {
  default: 'text-gray-400',
  success: 'text-emerald-500',
  warning: 'text-amber-600',
  info: 'text-plex',
  destructive: 'text-red-500',
};

const VARIANT_ICON: Record<AlertVariant, typeof AlertCircle> = {
  default: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
  destructive: AlertCircle,
};

const Alert = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    variant?: AlertVariant;
    /** Skrýt výchozí ikonu vlevo */
    hideIcon?: boolean;
    icon?: ReactNode;
  }
>(({ className, variant = 'default', hideIcon, icon, children, ...props }, ref) => {
  const Icon = VARIANT_ICON[variant];
  return (
    <div
      ref={ref}
      role='alert'
      className={cn(
        'relative w-full overflow-hidden rounded-2xl p-4 pl-4',
        VARIANT_SHELL[variant],
        className,
      )}
      {...props}
    >
      {/* levý accent pruh */}
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl',
          VARIANT_ACCENT[variant],
        )}
      />
      <div className='flex gap-3 pl-2'>
        {!hideIcon && (
          <span className={cn('mt-0.5 shrink-0', VARIANT_ICON_COLOR[variant])}>
            {icon ?? <Icon size={16} strokeWidth={2} />}
          </span>
        )}
        <div className='min-w-0 flex-1'>{children}</div>
      </div>
    </div>
  );
});
Alert.displayName = 'Alert';

const AlertTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 text-sm font-semibold leading-none tracking-tight text-gray-100', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-sm leading-relaxed text-gray-300 [&_p]:leading-relaxed', className)}
      {...props}
    />
  ),
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
